import React, { useContext, useEffect, useMemo, useState } from 'react'

import { Form, Formik } from 'formik'
import { useHistory } from 'react-router-dom'
import { toast } from 'react-toastify'
import * as Yup from 'yup'

import Dialog from '@material-ui/core/Dialog'
import DialogActions from '@material-ui/core/DialogActions'
import DialogContent from '@material-ui/core/DialogContent'
import DialogTitle from '@material-ui/core/DialogTitle'
import TextField from '@material-ui/core/TextField'

import { i18n } from '../../translate/i18n'

import { Grid } from '@material-ui/core'
import Autocomplete from '@material-ui/lab/Autocomplete'
import { capitalize, isArray } from 'lodash'
import moment from 'moment'
import { AuthContext } from '../../context/Auth/AuthContext'
import toastError from '../../errors/toastError'
import api from '../../services/api'
import { ButtonPrimary } from '../ButtonPrimary'
import { ButtonSecondary } from '../ButtonSecondary'
import { InputField } from '../InputField'
import { useStyles } from './style'
import { InputFieldText } from '../InputFieldText'

const ScheduleSchema = Yup.object().shape({
  body: Yup.string().min(5, 'Mensagem muito curta').required('Obrigatório'),
  contactId: Yup.number().required('Obrigatório'),
  sendAt: Yup.string().required('Obrigatório'),
  sendTime: Yup.string().required('Obrigatório'),
  saveMessage: Yup.bool()
})

const ScheduleModal = ({
  open,
  onClose,
  scheduleId,
  contactId,
  initialValues,
  cleanContact,
  reload
}) => {
  const classes = useStyles()
  const history = useHistory()
  const { user } = useContext(AuthContext)

  const initialState = useMemo(
    () => ({
      body: '',
      contactId: '',
      sendAt: '',
      sendTime: '',
      sentAt: '',
      saveMessage: false
    }),
    []
  )

  const initialContact = {
    id: '',
    name: ''
  }

  const [schedule, setSchedule] = useState(initialState)
  const [currentContact, setCurrentContact] = useState(initialContact)
  const [contacts, setContacts] = useState([initialContact])

  const formikRef = React.useRef()

  useEffect(() => {
    if (contactId && contacts.length) {
      const contact = contacts.find(c => c.id === contactId)
      if (contact) {
        setCurrentContact(contact)
      }
    }
  }, [contactId, contacts])

  useEffect(() => {
    if (!open) {
      setSchedule(initialState)
      setCurrentContact(initialContact)
      return
    }

    let isMounted = true

    const fetchData = async () => {
      try {
        const { companyId } = user

        const { data: contactList } = await api.get('/contacts/list', {
          params: { companyId }
        })

        if (!isMounted) return

        const customList = contactList.map(c => ({ id: c.id, name: c.name }))
        setContacts([{ id: '', name: '' }, ...customList])

        if (scheduleId) {
          const { data } = await api.get(`/schedules/${scheduleId}`)

          if (isMounted) {
            const formattedDate = moment(data.sendAt).format('YYYY-MM-DD')
            const formattedTime = moment(data.sendAt).format('HH:mm')

            const scheduleState = {
              ...data,
              sendAt: formattedDate,
              sendTime: formattedTime
            }

            setSchedule(scheduleState)
            setCurrentContact(data.contact || initialContact)

            if (formikRef.current) {
              formikRef.current.resetForm({ values: scheduleState })
            }
          }
        } else if (initialValues) {
          if (isMounted) {
            setSchedule(initialValues)
            setCurrentContact(initialValues.contact || initialContact)

            if (formikRef.current) {
              formikRef.current.resetForm({ values: initialValues })
            }
          }
        } else {
          const freshState = contactId
            ? { ...initialState, contactId }
            : initialState

          setSchedule(freshState)

          if (formikRef.current) {
            formikRef.current.resetForm({ values: freshState })
          }
        }
      } catch (err) {
        toastError(err)
      }
    }

    fetchData()

    return () => {
      isMounted = false
    }
  }, [open, scheduleId, initialValues, contactId, user])

  const handleClose = () => {
    onClose()
    setSchedule(initialState)
  }

  const handleSaveSchedule = async values => {
    const scheduleData = { ...values, userId: user.id }
    try {
      if (scheduleId) {
        await api.put(`/schedules/${scheduleId}`, scheduleData)
      } else {
        await api.post('/schedules', scheduleData)
      }
      toast.success(i18n.t('scheduleModal.success'))
      if (typeof reload == 'function') {
        reload()
      }
      if (contactId) {
        if (typeof cleanContact === 'function') {
          cleanContact()
          history.push('/schedules')
        }
      }
    } catch (err) {
      toastError(err)
    }
    setCurrentContact(initialContact)
    setSchedule(initialState)
    handleClose()
  }

  const handleTimeChange = (e, setFieldValue) => {
    setFieldValue('sendTime', e.target.value)
    e.target.blur()
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      scroll="paper"
      classes={{ paper: classes.dialog }}
      aria-labelledby="form-dialog-title"
    >
      <DialogTitle id="form-dialog-title" className={classes.dialogTitle}>
        {(() => {
          if (scheduleId) {
            return 'Editar Agendamento'
          }
          // Se o status for PENDENTE mas não houver ID, veio do fluxo handleCopySchedule
          if (schedule.status === 'PENDENTE' && schedule.id === null) {
            return 'Copiar Agendamento'
          }
          return 'Novo Agendamento'
        })()}
      </DialogTitle>
      <Formik
        innerRef={formikRef}
        initialValues={schedule}
        enableReinitialize={true}
        validationSchema={ScheduleSchema}
        onSubmit={(values, actions) => {
          const combineDateTime = moment(
            `${values.sendAt} ${values.sendTime}`,
            'YYYY-MM-DD HH:mm'
          ).format('YYYY-MM-DDTHH:mm')

          const scheduleData = {
            ...values,
            sendAt: combineDateTime,
            sendTime: undefined
          }

          setTimeout(() => {
            handleSaveSchedule(scheduleData)
            actions.setSubmitting(false)
          }, 400)
        }}
      >
        {({
          touched,
          errors,
          isSubmitting,
          values,
          setFieldValue,
          handleChange
        }) => (
          <Form>
            <DialogContent dividers>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={12} md={12}>
                  <Autocomplete
                    className={classes.inputAutocomplete}
                    size="small"
                    fullWidth
                    clearOnBlur
                    autoHighlight
                    freeSolo
                    clearOnEscape
                    value={currentContact}
                    options={contacts}
                    onChange={(e, contact) => {
                      const contactId = contact ? contact.id : ''
                      setSchedule({
                        ...schedule,
                        contactId
                      })
                      setCurrentContact(contact ? contact : initialContact)
                    }}
                    getOptionLabel={option => option.name}
                    getOptionSelected={(option, value) => {
                      return value.id === option.id
                    }}
                    disabled={contactId ? true : false}
                    renderInput={params => (
                      <>
                        <span>Digite para pesquisar um contato</span>
                        <TextField
                          {...params}
                          variant="outlined"
                          placeholder="Selecione um contato"
                        />
                      </>
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={12} md={12}>
                  <InputField
                    minRows={6}
                    multiline={true}
                    label={i18n.t('scheduleModal.form.body')}
                    name="body"
                    error={touched.body && Boolean(errors.body)}
                    helperText={touched.body && errors.body}
                    emoji
                  />
                </Grid>
                <Grid item xs={12} sm={9} md={9}>
                  <InputFieldText
                    label={i18n.t('scheduleModal.form.sendAt')}
                    type="date"
                    name="sendAt"
                    value={values.sendAt || ''}
                    onChange={handleChange}
                    InputLabelProps={{ shrink: true }}
                    error={touched.sendAt && Boolean(errors.sendAt)}
                    helperText={touched.sendAt && errors.sendAt}
                  />
                </Grid>
                <Grid item xs={12} sm={3} md={3}>
                  <InputFieldText
                    label="Hora"
                    className={classes.inputHour}
                    type="time"
                    variant="outlined"
                    name="sendTime"
                    fullWidth
                    onChange={e => {
                      handleChange(e) // Atualiza o Formik normalmente

                      const value = e.target.value
                      if (value && value.length === 5) {
                        e.target.blur() // Remove o foco do input, fechando a listagem de horas do navegador
                      }
                    }}
                    value={values.sendTime || ''}
                    InputLabelProps={{ shrink: true }}
                    error={touched.sendTime && Boolean(errors.sendTime)}
                    helperText={touched.sendTime && errors.sendTime}
                  />
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions>
              <ButtonSecondary
                onClick={handleClose}
                disabled={isSubmitting}
                label={i18n.t('scheduleModal.buttons.cancel')}
              />
              {/* {(schedule.sentAt === null || schedule.sentAt === '') && ( */}
              <ButtonPrimary
                type="submit"
                disabled={isSubmitting}
                className={classes.btnWrapper}
                label={i18n.t('scheduleModal.buttons.okAdd')}
              ></ButtonPrimary>
              {/* )} */}
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  )
}

export default ScheduleModal
